import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuth, selectUserRole } from '../../store/slices/authSlice';

/**
 * Route guard supporting two modes:
 *
 *  allowedRoles   – exact role values, e.g. ['CLIENT_ADMIN', 'CLIENT_USER']
 *  allowedGroups  – role-prefix groups: 'platform' | 'client' | 'vendor'
 *                   Grants access to ALL roles in that group (including VIEWER).
 *
 * You can combine both: a user passes if they match either condition.
 */
export default function ProtectedRoute({ children, allowedRoles, allowedGroups }) {
  const isAuth   = useSelector(selectIsAuth);
  const role     = useSelector(selectUserRole);
  const location = useLocation();

  if (!isAuth) return <Navigate to="/login" state={{ from: location }} replace />;

  const groupMatch = !allowedGroups || allowedGroups.some((g) => {
    if (g === 'platform') return role?.startsWith('PLATFORM_') || role === 'CLIENT_VENDOR_ADMIN';
    if (g === 'client')   return role?.startsWith('CLIENT_');
    if (g === 'vendor')   return role?.startsWith('VENDOR_');
    return false;
  });

  const roleMatch = !allowedRoles || allowedRoles.includes(role);

  // Must satisfy at least one non-empty constraint
  const hasConstraint = allowedRoles || allowedGroups;
  const allowed = !hasConstraint || groupMatch || roleMatch;

  if (!allowed) return <Navigate to="/login" replace />;
  return children;
}
